import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { CalendarDays, Clock, Radio, RefreshCw, Video } from 'lucide-react';
import axios from 'axios';
import { API } from '../App';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';

const fallbackContentByLocale = {
  el: {
    hero: {
      kicker: 'LIVE ΣΕΜΙΝΑΡΙΑ',
      title: 'Παρακολούθησε ζωντανά εκπαιδευτικά σεμινάρια και replays από το MISEL GROUP.',
      subtitle: 'Δες τα επερχόμενα live, μπες στα ενεργά σεμινάρια ή παρακολούθησε διαθέσιμα replays μέσα από το πλάνο σου ή με μεμονωμένη πρόσβαση.',
    },
    sections: {
      live_now_title: 'Ζωντανά τώρα',
      upcoming_title: 'Επερχόμενα Live Σεμινάρια',
      replays_title: 'Replays',
      empty_title: 'Δεν υπάρχουν δημοσιευμένα live σεμινάρια ακόμα',
      empty_body: 'Τα επερχόμενα live σεμινάρια θα εμφανιστούν εδώ μόλις προγραμματιστούν.',
      no_live_now: 'Δεν υπάρχει ενεργό live αυτή τη στιγμή.',
      no_upcoming: 'Δεν υπάρχουν προγραμματισμένα live σεμινάρια ακόμα.',
      no_replays: 'Δεν υπάρχουν διαθέσιμα replays ακόμα.',
      load_error: 'Δεν ήταν δυνατή η φόρτωση των live σεμιναρίων. Δοκίμασε ξανά σε λίγο.',
    },
    buttons: {
      watch_live: 'Παρακολούθηση Live',
      watch_replay: 'Παρακολούθηση Replay',
      view_details: 'Προβολή σεμιναρίου',
    },
    labels: {
      one_time_access: 'Μεμονωμένη πρόσβαση',
      includes_replay: 'Η μεμονωμένη πρόσβαση περιλαμβάνει replay όταν δημοσιευτεί.',
      date_tba: 'Η ημερομηνία θα ανακοινωθεί',
      ends: 'Λήξη',
    },
  },
  en: {
    hero: {
      kicker: 'Live seminars',
      title: 'Watch live education and replay sessions from MISEL GROUP.',
      subtitle: 'Find upcoming streams, join live sessions, or return to published replays.',
    },
    sections: {
      live_now_title: 'Live Now',
      upcoming_title: 'Upcoming Live Seminars',
      replays_title: 'Replays',
      empty_title: 'No live seminars are published yet',
      empty_body: 'Upcoming live seminars will appear here as soon as they are scheduled.',
      no_live_now: 'No seminars are live right now.',
      no_upcoming: 'No upcoming live seminars are scheduled yet.',
      no_replays: 'No replays are available yet.',
      load_error: 'Live seminars could not be loaded. Please try again shortly.',
    },
    buttons: {
      watch_live: 'Watch Live',
      watch_replay: 'Watch Replay',
      view_details: 'View Details',
    },
    labels: {
      one_time_access: 'One-time access',
      includes_replay: 'One-time access includes replay when published.',
      date_tba: 'Date to be announced',
      ends: 'Ends',
    },
  },
};

const statusClasses = (seminar) => {
  if (seminar.status === 'live') return 'bg-red-500/15 text-red-300 border-red-500/30';
  if (seminar.replay_available || seminar.status === 'published') return 'bg-primary/15 text-primary border-primary/30';
  return 'bg-white/10 text-gray-300 border-white/15';
};

const formatDate = (value, labels) => {
  if (!value) return labels.date_tba;
  return new Date(value).toLocaleString(undefined, {
    weekday: 'short',
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const LiveSeminarCard = ({ seminar, index, copy }) => {
  const ppv = seminar.ppv_purchase;
  const buttonLabel = seminar.status === 'live'
    ? copy.buttons.watch_live
    : seminar.replay_available || seminar.status === 'published'
      ? copy.buttons.watch_replay
      : copy.buttons.view_details;
  const badgeLabel = seminar.status === 'live'
    ? copy.sections.live_now_title
    : seminar.replay_available || seminar.status === 'published'
      ? copy.sections.replays_title
      : copy.sections.upcoming_title;

  return (
    <motion.article
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, delay: index * 0.05 }}
      className="bg-misel-card border border-white/10 rounded-sm overflow-hidden flex flex-col min-w-0"
    >
      <Link to={`/live/${seminar.slug}`} className="block aspect-video bg-black/40 overflow-hidden">
        {seminar.cover_image_url ? (
          <img
            src={seminar.cover_image_url}
            alt={seminar.title}
            className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-black via-misel-card to-[#1f0b0b]">
            <Radio size={34} className="text-primary" />
          </div>
        )}
      </Link>

      <div className="p-5 flex flex-col flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <span className={`inline-flex items-center gap-1 rounded-sm border px-2 py-1 text-xs font-semibold uppercase tracking-wide ${statusClasses(seminar)}`}>
            {seminar.status === 'live' ? <Radio size={11} className="animate-pulse" /> : <Video size={11} />}
            {badgeLabel}
          </span>
          {ppv && (
            <span className="text-xs text-primary bg-primary/10 border border-primary/20 rounded-sm px-2 py-1">
              {copy.labels.one_time_access} {ppv.amount?.toFixed ? ppv.amount.toFixed(2) : ppv.amount} {ppv.currency || 'EUR'}
            </span>
          )}
        </div>

        <h3 className="font-roboto font-bold text-xl text-white leading-tight break-words">
          {seminar.title}
        </h3>
        {seminar.description && (
          <p className="mt-2 text-sm text-gray-400 leading-relaxed line-clamp-3 break-words">
            {seminar.description}
          </p>
        )}

        <div className="mt-4 space-y-2 text-sm text-gray-500">
          <p className="flex items-center gap-2">
            <CalendarDays size={14} className="text-primary flex-shrink-0" />
            <span className="break-words">{formatDate(seminar.scheduled_start_at, copy.labels)}</span>
          </p>
          {seminar.scheduled_end_at && (
            <p className="flex items-center gap-2">
              <Clock size={14} className="text-primary flex-shrink-0" />
              <span className="break-words">{copy.labels.ends} {formatDate(seminar.scheduled_end_at, copy.labels)}</span>
            </p>
          )}
        </div>

        {seminar.access_plan_names?.length > 0 && (
          <div className="mt-4 flex flex-wrap gap-2">
            {seminar.access_plan_names.map((name) => (
              <span key={name} className="text-xs text-gray-300 bg-white/5 rounded-sm px-2 py-1 break-words">
                {name}
              </span>
            ))}
          </div>
        )}

        {ppv?.includes_replay && (
          <p className="mt-3 text-xs text-gray-500 whitespace-pre-line break-words">{copy.labels.includes_replay}</p>
        )}

        <Button asChild className="mt-5 w-full bg-primary hover:bg-primary/90">
          <Link to={`/live/${seminar.slug}`}>{buttonLabel}</Link>
        </Button>
      </div>
    </motion.article>
  );
};

const LiveSection = ({ title, items, emptyText, copy }) => (
  <section className="space-y-5">
    <div className="flex items-center justify-between gap-4">
      <h2 className="font-roboto font-bold text-2xl text-white tracking-wide">{title}</h2>
      <span className="text-xs text-gray-500">{items.length}</span>
    </div>
    {items.length > 0 ? (
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
        {items.map((seminar, index) => (
          <LiveSeminarCard key={seminar.id} seminar={seminar} index={index} copy={copy} />
        ))}
      </div>
    ) : (
      <div className="border border-white/10 bg-misel-card rounded-sm p-6 text-gray-500 text-sm">
        {emptyText}
      </div>
    )}
  </section>
);

const LiveSeminarsPage = () => {
  const { i18n } = useTranslation();
  const [seminars, setSeminars] = useState([]);
  const [contentPage, setContentPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const fetchSeminars = async () => {
      try {
        const [res, contentRes] = await Promise.all([
          axios.get(`${API}/live-seminars`),
          axios.get(`${API}/content/pages/live?locale=${i18n.language}`).catch(() => ({ data: null })),
        ]);
        setSeminars(res.data?.live_seminars || []);
        setContentPage(contentRes.data);
      } catch (_e) {
        setError(true);
      } finally {
        setLoading(false);
      }
    };
    fetchSeminars();
  }, [i18n.language]);

  const grouped = useMemo(() => ({
    live: seminars.filter((item) => item.status === 'live'),
    upcoming: seminars.filter((item) => item.status === 'scheduled'),
    replays: seminars.filter((item) => item.replay_available || item.status === 'published'),
  }), [seminars]);
  const fallbackContent = i18n.language?.toLowerCase().startsWith('el')
    ? fallbackContentByLocale.el
    : fallbackContentByLocale.en;
  const copy = {
    hero: { ...fallbackContent.hero, ...(contentPage?.sections?.hero || {}) },
    sections: { ...fallbackContent.sections, ...(contentPage?.sections?.sections || {}) },
    buttons: { ...fallbackContent.buttons, ...(contentPage?.sections?.buttons || {}) },
    labels: { ...fallbackContent.labels, ...(contentPage?.sections?.labels || {}) },
  };

  return (
    <div className="min-h-screen bg-misel-bg">
      <Navbar />
      <main className="pt-24 px-4 sm:px-8 lg:px-16 pb-16">
        <div className="max-w-7xl mx-auto space-y-12">
          <div className="max-w-3xl">
            <p className="text-primary text-xs font-bold uppercase tracking-[0.25em] mb-3">{copy.hero.kicker}</p>
            <h1 className="font-roboto font-bold text-3xl md:text-5xl text-white leading-tight break-words">
              {copy.hero.title}
            </h1>
            <p className="mt-4 text-gray-400 leading-relaxed whitespace-pre-line break-words">
              {copy.hero.subtitle}
            </p>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {[1, 2, 3].map((item) => (
                <div key={item} className="h-80 bg-misel-card animate-pulse rounded-sm" />
              ))}
            </div>
          ) : error ? (
            <div className="border border-red-500/30 bg-red-500/10 rounded-sm p-6 text-red-200">
              {copy.sections.load_error}
            </div>
          ) : seminars.length === 0 ? (
            <div className="border border-white/10 bg-misel-card rounded-sm p-10 text-center">
              <RefreshCw size={36} className="mx-auto text-primary mb-4" />
              <h2 className="text-white font-bold text-xl mb-2 break-words">{copy.sections.empty_title}</h2>
              <p className="text-gray-500 text-sm max-w-md mx-auto">
                {copy.sections.empty_body}
              </p>
            </div>
          ) : (
            <>
              {grouped.live.length > 0 && (
                <LiveSection title={copy.sections.live_now_title} items={grouped.live} emptyText={copy.sections.no_live_now} copy={copy} />
              )}
              <LiveSection
                title={copy.sections.upcoming_title}
                items={grouped.upcoming}
                emptyText={copy.sections.no_upcoming}
                copy={copy}
              />
              {grouped.replays.length > 0 && (
                <LiveSection title={copy.sections.replays_title} items={grouped.replays} emptyText={copy.sections.no_replays} copy={copy} />
              )}
            </>
          )}
        </div>
      </main>
    </div>
  );
};

export default LiveSeminarsPage;
